/**
 * Retiring a device, and the Learn articles retirements leave behind.
 *
 * RetireDeviceDialog records why and when a device left (retire_rack_unit
 * turns its position into a vacancy and keeps a snapshot). RetirementsDrawer
 * lists a site's retirements; each one carries its ready-to-file Learn
 * article — manual, chapter, title and body to copy — and a field for the
 * article's link once staff have filed it. PazPortal never writes to Learn.
 */
import { useEffect, useMemo, useState } from 'react';
import { cn } from '@pazportal/ui';
import { ruLabel, type RackPlant } from '../../lib/rackModel';
import {
  DISPOSITION_LABEL,
  REASON_LABEL,
  RETIREMENT_DISPOSITIONS,
  RETIREMENT_REASONS,
  chapterForKind,
  manualSlugFromLearnUrl,
  retiredFrom,
  retirementArticleHtml,
  retirementArticleText,
  retirementTitle,
  type RetirementDisposition,
  type RetirementReason,
  type RetirementRow,
} from '../../lib/retirementArticle';
import {
  useDeviceRetirements,
  useMarkRetirementFiled,
  useRetireDevice,
  type PropertyManual,
} from '../../hooks/useDeviceRetirements';

/** Today in the viewer's time zone (UTC would read tomorrow on a US evening). */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function manualName(manual: PropertyManual | undefined): string {
  const slug = manualSlugFromLearnUrl(manual?.learnUrl);
  return slug ? slug.toUpperCase() : (manual?.code ?? 'this property');
}

export function RetireDeviceDialog({ plant, unitId, locationId, manual, onClose, onRetired }: {
  plant: RackPlant;
  unitId: string;
  locationId: string;
  manual: PropertyManual | undefined;
  onClose: () => void;
  onRetired: (retirementId: string) => void;
}) {
  const unit = plant.unitById.get(unitId);
  const rack = unit ? plant.rackById.get(unit.rack_id) : undefined;
  const room = rack ? plant.roomById.get(rack.room_id) : undefined;
  const [retiredOn, setRetiredOn] = useState(today);
  const [reason, setReason] = useState<RetirementReason>('replaced');
  const [disposition, setDisposition] = useState<RetirementDisposition>('unknown');
  const [replacement, setReplacement] = useState('');
  const [note, setNote] = useState('');
  const retire = useRetireDevice(locationId);
  useEscape(onClose);

  if (!unit || !rack) return null;
  const networks = plant.networksByUnit.get(unit.id) ?? [];
  const links = plant.linksByUnit.get(unit.id) ?? [];
  const where = `${room ? (room.name ? `${room.code} · ${room.name}` : room.code) : ''} · ${rack.code} · ${ruLabel(unit)}`;

  return (
    <Overlay onClose={onClose} labelledBy="retire-title" side="center">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          retire.mutate(
            { unitId, retiredOn, reason, disposition, replacement, note },
            { onSuccess: (id) => onRetired(id) },
          );
        }}
      >
        <header>
          <p className="text-xs text-text-faint">Retire device</p>
          <h2 id="retire-title" className="text-lg font-semibold text-text">{unit.label}</h2>
          <p className="font-mono text-xs text-text-muted">{where}</p>
        </header>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Label text="Retired on">
            <input type="date" required max={today()} value={retiredOn} onChange={(e) => setRetiredOn(e.target.value)} className={INPUT} />
          </Label>
          <Label text="Reason">
            <select value={reason} onChange={(e) => setReason(e.target.value as RetirementReason)} className={INPUT}>
              {RETIREMENT_REASONS.map((r) => <option key={r} value={r}>{REASON_LABEL[r]}</option>)}
            </select>
          </Label>
          <Label text="Where it went">
            <select value={disposition} onChange={(e) => setDisposition(e.target.value as RetirementDisposition)} className={INPUT}>
              {RETIREMENT_DISPOSITIONS.map((d) => <option key={d} value={d}>{DISPOSITION_LABEL[d]}</option>)}
            </select>
          </Label>
          <Label text="Replaced by (optional)">
            <input value={replacement} onChange={(e) => setReplacement(e.target.value)} placeholder="e.g. Netgear M4350-24X4V" className={INPUT} />
          </Label>
        </div>
        <Label text="Notes (optional)">
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={cn(INPUT, 'h-auto py-1.5')} />
        </Label>

        <div className="space-y-1.5 rounded-sm border border-border bg-surface-2 p-3 text-xs text-text">
          <p className="font-semibold">What happens</p>
          <ul className="list-disc space-y-0.5 pl-4 text-text-muted">
            <li>{ruLabel(unit)} in {rack.code} becomes a verified vacancy.</li>
            <li>
              {networks.length ? `It leaves ${networks.length} network${networks.length === 1 ? '' : 's'}` : 'It is in no recorded network'}
              {' and '}
              {links.length ? `${links.length} recorded connection${links.length === 1 ? ' is' : 's are'} removed.` : 'has no recorded connections.'}
            </li>
            {unit.asset_id && <li>Its linked asset is marked decommissioned.</li>}
            <li>Everything recorded about it is kept on the retirement.</li>
            <li>
              A Learn article is prepared for the <span className="font-semibold text-text">{manualName(manual)}</span> manual,
              chapter <span className="font-semibold text-text">{chapterForKind(unit.kind)}</span>, for you to file as a draft.
            </li>
          </ul>
        </div>

        {retire.isError && <p className="text-sm text-error">{(retire.error as Error).message}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={BTN_GHOST}>Cancel</button>
          <button type="submit" disabled={retire.isPending} className={BTN_RETIRE}>
            {retire.isPending ? 'Retiring…' : 'Retire device'}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

export function RetirementsDrawer({ locationId, manual, focusId, onClose }: {
  locationId: string;
  manual: PropertyManual | undefined;
  focusId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useDeviceRetirements(locationId);
  useEscape(onClose);
  const pending = (data ?? []).filter((r) => !r.learn_article_url).length;

  return (
    <Overlay onClose={onClose} labelledBy="retirements-title" side="right">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-text-faint">Rack history</p>
          <h2 id="retirements-title" className="text-lg font-semibold text-text">Retired devices</h2>
          {data && data.length > 0 && (
            <p className="text-xs text-text-muted">
              {data.length} retired · {pending ? `${pending} not yet filed in Learn` : 'all filed in Learn'}
            </p>
          )}
        </div>
        <button onClick={onClose} className={BTN_GHOST} aria-label="Close">Close</button>
      </header>
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-error">Retirements could not be read. Refresh to try again.</p>
      ) : !data?.length ? (
        <p className="text-sm text-text-muted">No device has been retired at this site. Retire one from its row in a rack.</p>
      ) : (
        <ul className="space-y-3">
          {data.map((r) => (
            <RetirementCard key={r.id} retirement={r} manual={manual} locationId={locationId} defaultOpen={r.id === focusId} />
          ))}
        </ul>
      )}
    </Overlay>
  );
}

function RetirementCard({ retirement: r, manual, locationId, defaultOpen }: {
  retirement: RetirementRow;
  manual: PropertyManual | undefined;
  locationId: string;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen || !r.learn_article_url);
  const title = useMemo(() => retirementTitle(r), [r]);
  const chapter = chapterForKind(r.snapshot.unit.kind);
  const filed = !!r.learn_article_url;

  return (
    <li className={cn('rounded-sm border bg-bg-elevated', defaultOpen ? 'border-primary' : 'border-border')}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-start justify-between gap-3 p-3 text-left">
        <span className="min-w-0">
          <span className="block truncate font-semibold text-text">{r.snapshot.unit.label}</span>
          <span className="block font-mono text-[11px] text-text-muted">{retiredFrom(r.snapshot)} · {r.retired_on} · {REASON_LABEL[r.reason]}</span>
        </span>
        <span className={cn('flex-none rounded-sm px-1.5 py-0.5 text-[10.5px] font-medium', filed ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning')}>
          {filed ? 'Filed in Learn' : 'Not filed'}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-divider p-3">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <Field label="Where it went" value={DISPOSITION_LABEL[r.disposition]} />
            <Field label="Replaced by" value={r.replacement ?? '—'} />
            {r.snapshot.unit.ip_address && <Field label="IP" value={r.snapshot.unit.ip_address} />}
            {r.snapshot.unit.mac_address && <Field label="MAC" value={r.snapshot.unit.mac_address} />}
          </dl>
          <LearnDraft retirement={r} title={title} chapter={chapter} manual={manual} />
          <FiledLink retirement={r} locationId={locationId} />
        </div>
      )}
    </li>
  );
}

function LearnDraft({ retirement, title, chapter, manual }: {
  retirement: RetirementRow;
  title: string;
  chapter: string;
  manual: PropertyManual | undefined;
}) {
  const [copied, setCopied] = useState<'title' | 'body' | null>(null);
  const copy = async (what: 'title' | 'body') => {
    if (what === 'title') await navigator.clipboard.writeText(title);
    else await copyRich(retirementArticleHtml(retirement), retirementArticleText(retirement));
    setCopied(what);
    setTimeout(() => setCopied(null), 1500);
  };
  return (
    <div className="space-y-2 rounded-sm border border-border bg-surface-2 p-3">
      <p className="text-xs font-semibold text-text">Learn article</p>
      <ol className="list-decimal space-y-0.5 pl-4 text-xs text-text-muted">
        <li>
          In the <span className="font-semibold text-text">{manualName(manual)}</span> manual
          {manual?.learnUrl && (
            <> (<a href={manual.learnUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">open</a>)</>
          )}
          , add an article to chapter <span className="font-semibold text-text">{chapter}</span>.
        </li>
        <li>Paste the title and the article below. Leave it as a draft.</li>
        <li>Publish after review. The customer sees it in Documents from then on.</li>
        <li>Paste the article’s link below.</li>
      </ol>
      {!manual?.learnUrl && (
        <p className="text-xs text-warning">This property has no Learn manual linked in the property directory.</p>
      )}
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-sm bg-bg px-2 py-1 text-[11.5px] text-text">{title}</code>
        <button type="button" onClick={() => void copy('title')} className={BTN_GHOST}>{copied === 'title' ? 'Copied' : 'Copy title'}</button>
      </div>
      <div className="max-h-48 overflow-auto rounded-sm border border-divider bg-bg p-2 text-[11.5px] text-text [&_h2]:mt-2 [&_h2]:font-semibold [&_table]:w-full [&_td]:pl-3 [&_th]:pr-2 [&_th]:text-left [&_th]:font-normal [&_th]:text-text-muted [&_ul]:list-disc [&_ul]:pl-4"
        // Built from escaped snapshot values in retirementArticleHtml().
        dangerouslySetInnerHTML={{ __html: retirementArticleHtml(retirement) }}
      />
      <div className="flex justify-end">
        <button type="button" onClick={() => void copy('body')} className={BTN_PRIMARY}>{copied === 'body' ? 'Copied' : 'Copy article'}</button>
      </div>
    </div>
  );
}

function FiledLink({ retirement: r, locationId }: { retirement: RetirementRow; locationId: string }) {
  const [url, setUrl] = useState(r.learn_article_url ?? '');
  const mark = useMarkRetirementFiled(locationId);
  useEffect(() => setUrl(r.learn_article_url ?? ''), [r.learn_article_url]);
  const dirty = url.trim() !== (r.learn_article_url ?? '');
  return (
    <form
      className="space-y-1"
      onSubmit={(e) => {
        e.preventDefault();
        mark.mutate({ id: r.id, url: url.trim() || null });
      }}
    >
      <Label text="Learn article link">
        <div className="flex gap-2">
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://learn.paznetworks.com/…" className={cn(INPUT, 'flex-1')} />
          <button type="submit" disabled={!dirty || mark.isPending} className={BTN_GHOST}>{mark.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </Label>
      {r.learn_article_url && !dirty && (
        <a href={r.learn_article_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Open the filed article ↗</a>
      )}
      {mark.isError && <p className="text-xs text-error">{(mark.error as Error).message}</p>}
    </form>
  );
}

/** Copies HTML for rich editors (Learn), with plain text as the fallback. */
async function copyRich(html: string, text: string) {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
  } catch {
    await navigator.clipboard.writeText(text);
  }
}

function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

function Overlay({ children, onClose, labelledBy, side }: {
  children: React.ReactNode;
  onClose: () => void;
  labelledBy: string;
  side: 'center' | 'right';
}) {
  return (
    <div
      className={cn('fixed inset-0 z-[60] flex bg-black/40', side === 'center' ? 'items-center justify-center p-4' : 'items-end sm:items-stretch sm:justify-end')}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'overflow-y-auto border-border bg-surface p-5 shadow-xl',
          side === 'center'
            ? 'max-h-[90vh] w-full max-w-lg rounded-lg border'
            : 'max-h-[85vh] w-full rounded-t-lg border-t sm:h-full sm:max-h-none sm:w-[460px] sm:rounded-none sm:border-l sm:border-t-0',
        )}
      >
        {children}
      </div>
    </div>
  );
}

function Label({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-medium uppercase tracking-wider text-text-muted">{text}</span>
      {children}
    </label>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className="m-0 truncate font-mono text-text">{value}</dd>
    </div>
  );
}

const INPUT = 'h-8 w-full rounded-sm border border-border bg-bg px-2 text-sm text-text focus:border-primary focus:outline-none';
const BTN_GHOST = 'h-8 flex-none rounded-sm border border-border bg-bg-elevated px-3 text-sm text-text hover:bg-surface-3 disabled:opacity-50';
const BTN_PRIMARY = 'h-8 flex-none rounded-sm bg-primary px-3 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-50';
const BTN_RETIRE = 'h-8 flex-none rounded-sm bg-error px-3 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-50';
